import { useEffect } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { SingularConfig } from 'singular-sdk'
import { useSettings } from 'core/hooks/settings/useSettings'

/**
 * Bootstraps the Singular Web SDK on app load (EVC-2253, REQ-EVC-2253-002).
 *
 * When the `ENABLE_SINGULAR` feature flag is enabled, builds the Singular config
 * and initializes the SDK exactly once via the global `window.singularSdk.init`.
 */
export function useSingularInit(): void {
  const {
    data: {
      featureFlags: { ENABLE_SINGULAR }
    }
  } = useSettings()

  useEffect(() => {
    if (!ENABLE_SINGULAR) return

    const config = new SingularConfig(
      import.meta.env.VITE_APP_SINGULAR_SDK_KEY,
      import.meta.env.VITE_APP_SINGULAR_SECRET,
      import.meta.env.VITE_APP_SINGULAR_PRODUCT_ID
    ).withAutoPersistentSingularDeviceId('.everlance.com')

    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined

    try {
      window.singularSdk.init(config) // attempt 1
    } catch {
      retryTimer = setTimeout(() => {
        if (cancelled) return
        try {
          window.singularSdk.init(config) // attempt 2 (single retry)
        } catch (retryError) {
          datadogLogs.logger.warn('singular_init_failed', {
            error: String(retryError),
            attempts: 2
          })
          // swallow — fail-silent (REQ-016)
        }
      }, 3000)
    }

    return () => {
      cancelled = true
      clearTimeout(retryTimer)
    }
  }, [ENABLE_SINGULAR])
}
